import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

interface User {
  id: number;
  username: string;
  role: string;
  discord_id?: string;
  discord_roles?: string[];
}

// Staff member interface
interface StaffMember {
  id: string;
  username: string;
  displayName: string;
  rank: 'Founder' | 'Management' | 'Admin';
  playfabId: string;
  recruitmentDate: string | null;
  status: 'Active' | 'Exempt' | 'Inactive' | 'On Leave';
  isActive: boolean;
  avatar: string | null;
}

// Past staff member interface
interface PastStaffMember {
  id: string;
  rank: string;
  name: string;
  playfabID: string;
  recruitmentDate: string;
  removalDate: string;
  removalReason: string;
}

// Game server interface
interface GameServer {
  id: number;
  name: string;
  game: string;
  ip: string;
  port: number;
  queryType: 'gamedig' | 'source' | 'rcon';
  rconPassword?: string;
  rconMaxPlayers?: number;
  status: 'online' | 'offline' | 'maintenance';
  playerCount: number;
  maxPlayers: number;
  description: string;
  created_at: string;
  updated_at: string;
}

// Webhook interface
interface Webhook {
  id: number;
  server_id: number;
  name: string;
  type: string; // Now dynamic - can be any category name
  emoji: string; // Emoji for the category
  url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Log entry interface
interface LogEntry {
  id: number;
  server_id: number;
  server_name: string;
  type: string; // Now dynamic - matches webhook category names
  log_type?: string; // Added by API for separate table queries
  player_name: string;
  player_id: string;
  message: string;
  timestamp: string;
  metadata: any;
}

// Role hierarchy - higher numbers = higher permissions
const ROLE_PERMISSIONS: { [key: string]: number } = {
  '1394520034700693534': 3, // Founder - highest level
  '765079181666156545': 2,  // Management - second level
  '885301651538329651': 1,  // Admin - lowest level
};

// Dashboard sections with required permission levels
const DASHBOARD_SECTIONS = [
  {
    id: 'staff-info',
    title: 'Staff Information',
    description: 'Access staff guidelines and information',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-indigo-500',
    buttonColor: 'bg-indigo-600 hover:bg-indigo-700',
    buttonText: 'View Info',
    requiredLevel: 1, // Admin level
  },
  {
    id: 'staff-roster',
    title: 'Staff Roster',
    description: 'View and manage the current staff team',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    bgColor: 'bg-purple-500',
    buttonColor: 'bg-purple-600 hover:bg-purple-700',
    buttonText: 'View Roster',
    requiredLevel: 1, // Admin level - can view
  },
  {
    id: 'discord',
    title: 'Discord',
    description: 'Review Discord logs and moderate the server',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    bgColor: 'bg-blue-500',
    buttonColor: 'bg-blue-600 hover:bg-blue-700',
    buttonText: 'View Discord',
    requiredLevel: 2, // Management level
  },
  {
    id: 'logs',
    title: 'Servers & Logs',
    description: 'Manage servers and view logs',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    bgColor: 'bg-green-500',
    buttonColor: 'bg-green-600 hover:bg-green-700',
    buttonText: 'Manage Servers & Logs',
    requiredLevel: 1, // Admin level - can view, Management+ can edit
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Configure website and community settings',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    bgColor: 'bg-gray-500',
    buttonColor: 'bg-gray-600 hover:bg-gray-700',
    buttonText: 'Open Settings',
    requiredLevel: 3, // Founder level
  },

];

const Admin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loginWithStaffToken, loading, logout } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [staffRoster, setStaffRoster] = useState<StaffMember[]>([]);
  const [pastStaff, setPastStaff] = useState<PastStaffMember[]>([]);
  // const [isEditingStaff, setIsEditingStaff] = useState(false);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showRemoveStaffModal, setShowRemoveStaffModal] = useState(false);
  const [removingMember, setRemovingMember] = useState<StaffMember | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [showEditPastStaffModal, setShowEditPastStaffModal] = useState(false);
  const [editingPastMember, setEditingPastMember] = useState<PastStaffMember | null>(null);
  const [showRemovePastStaffModal, setShowRemovePastStaffModal] = useState(false);
  const [removingPastMember, setRemovingPastMember] = useState<PastStaffMember | null>(null);
  const [editingPastStaffData, setEditingPastStaffData] = useState({
    name: '',
    rank: 'Admin' as 'Founder' | 'Management' | 'Admin',
    playfabID: '',
    recruitmentDate: '',
    removalReason: ''
  });
  const [isSavingPastStaff, setIsSavingPastStaff] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'Founder' | 'Management' | 'Admin'>('Admin');
  const [addingRoleUserId, setAddingRoleUserId] = useState<string | null>(null);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [editingStaffData, setEditingStaffData] = useState({
    playfabId: '',
    recruitmentDate: '',
    rank: 'Admin' as 'Founder' | 'Management' | 'Admin',
    status: 'Active' as 'Active' | 'Exempt' | 'Inactive' | 'On Leave'
  });
  const [isSavingStaff, setIsSavingStaff] = useState(false);

  const [isRemovingStaff, setIsRemovingStaff] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Logs state
  const [gameServers, setGameServers] = useState<GameServer[]>([]);
  const [selectedServer, setSelectedServer] = useState<GameServer | null>(null);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showAddServerModal, setShowAddServerModal] = useState(false);
  const [showEditServerModal, setShowEditServerModal] = useState(false);
  const [showDeleteServerModal, setShowDeleteServerModal] = useState(false);
  const [showAddWebhookModal, setShowAddWebhookModal] = useState(false);
  const [showEditWebhookModal, setShowEditWebhookModal] = useState(false);
  const [editingServer, setEditingServer] = useState<GameServer | null>(null);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [serverForm, setServerForm] = useState({
    name: '',
    game: '',
    ip: '',
    port: 25565,
    queryType: 'gamedig' as 'gamedig' | 'source' | 'rcon',
    rconPassword: '',
    rconMaxPlayers: 100,
    description: ''
  });
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    type: '',
    emoji: '',
    url: ''
  });
  const [isSavingServer, setIsSavingServer] = useState(false);
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState<string>('all');
  const [logFilters, setLogFilters] = useState({
    search: '',
    player: '',
    dateFrom: '',
    dateTo: '',
    type: 'all'
  });
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [refreshFeedback, setRefreshFeedback] = useState('');

  // Quick Actions state
  const [quickAction, setQuickAction] = useState('');
  const [targetPlayFabId, setTargetPlayFabId] = useState('');
  const [targetPlayerName, setTargetPlayerName] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('');
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const [actionOutput, setActionOutput] = useState('');
  
  // Player search state
  const [playerSearchResults, setPlayerSearchResults] = useState<any[]>([]);
  const [isSearchingPlayers, setIsSearchingPlayers] = useState(false);
  
  // Separate search states for LookupPlayer and Quick Actions
  const [lookupSearchResults, setLookupSearchResults] = useState<any[]>([]);
  const [isSearchingLookup, setIsSearchingLookup] = useState(false);
  
  // Player lookup state
  const [lookupPlayFabId, setLookupPlayFabId] = useState('');
  const [lookupOutput, setLookupOutput] = useState('');
  const [isLookingUpPlayer, setIsLookingUpPlayer] = useState(false);
  
  // RCON Command Line state
  const [rconCommand, setRconCommand] = useState('');
  const [rconOutput, setRconOutput] = useState('');
  const [isExecutingRcon, setIsExecutingRcon] = useState(false);
  


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // const [showExportModal, setShowExportModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Staff Info state
  const [documents, setDocuments] = useState<any[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showCreateDocumentModal, setShowCreateDocumentModal] = useState(false);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [showViewDocumentModal, setShowViewDocumentModal] = useState(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState<any>(null);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    content: '',
    category: 'general',
    access_level: 'Admin' as 'Admin' | 'Management' | 'Founder'
  });
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState<string[]>([]);

  // Discord Stats state
  const [discordStats, setDiscordStats] = useState<any>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [moderationLogs, setModerationLogs] = useState<any[]>([]);
  const [isLoadingDiscordData, setIsLoadingDiscordData] = useState(false);

  // Click outside handler for export dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Get user's permission level based on their Discord roles
  const getUserPermissionLevel = (user: User): number => {
    if (!user.discord_roles || user.discord_roles.length === 0) {
      console.log('No Discord roles found for user:', user.username);
      return 0;
    }
    
    // Check for the highest permission level the user has
    let highestLevel = 0;
    const validRoles: string[] = [];
    
    user.discord_roles.forEach(roleId => {
      const level = ROLE_PERMISSIONS[roleId];
      if (level) {
        validRoles.push(roleId);
        if (level > highestLevel) {
          highestLevel = level;
        }
      }
    });
    
    // Only log if there are no valid roles or if this is the first time calculating
    if (validRoles.length === 0) {
      console.log('User permission calculation - no valid roles:', {
      username: user.username,
        allRoles: user.discord_roles
      });
    }
    
    return highestLevel;
  };

  // Get user's highest role name for display
  const getUserRoleName = (user: User): string => {
    const level = getUserPermissionLevel(user);
    switch(level) {
      case 3: return 'Founder';
      case 2: return 'Management';
      case 1: return 'Admin';
      default: return 'No Access';
    }
  };

  // Check if user has permission for a specific section
  const hasPermission = (requiredLevel: number): boolean => {
    if (!user) return false;
    const userLevel = getUserPermissionLevel(user);
    
    // Only log if user doesn't have permission
    if (userLevel < requiredLevel) {
      console.log('Permission denied:', { 
      userRoles: user.discord_roles, 
      userLevel, 
      requiredLevel, 
        hasPermission: false 
    });
    }
    
    return userLevel >= requiredLevel;
  };

  const verifyToken = useCallback(async (token: string) => {
    try {
      const response = await fetch('/api/discord-auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Use the AuthContext to set the user
        loginWithStaffToken(token, data.user);
        // Remove token from URL
        const currentPath = window.location.pathname;
        window.history.replaceState({}, document.title, currentPath);
      } else {
        // Token is invalid, redirect to staff login
        navigate('/staff-login');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      navigate('/staff-login');
    } finally {
      setIsProcessing(false);
    }
  }, [loginWithStaffToken, navigate, setIsProcessing]);

  const refreshUserRoles = useCallback(async () => {
    try {
      const response = await fetch('/api/discord-auth/verify', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('staffToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the user with fresh Discord roles
        loginWithStaffToken(localStorage.getItem('staffToken') || '', data.user);
      }
    } catch (error) {
      console.error('Error refreshing user roles:', error);
    }
  }, [loginWithStaffToken]);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (token && !user) {
      setIsProcessing(true);
      // Verify the token and get user info
      verifyToken(token);
    } else if (!user && !loading) {
      // No token and no user, redirect to staff login
      navigate('/staff-login');
    }
  }, [searchParams, user, loading, navigate, verifyToken]);

  // Refresh user's Discord roles on every page load
  useEffect(() => {
    console.log('Admin role refresh effect:', { 
      hasUser: !!user, 
      hasDiscordId: !!user?.discord_id, 
      isProcessing, 
      currentPath: window.location.pathname 
    });
    
    if (user && user.discord_id && !isProcessing) {
      // Don't refresh roles if we're on staff-login page (user might be logging out)
      if (window.location.pathname === '/staff-login') {
        console.log('Skipping role refresh - on staff-login page');
        return;
      }
      
      // Only refresh roles once when the component mounts or user changes
      // Don't refresh if we already have Discord roles
      if (user.discord_roles && user.discord_roles.length > 0) {
        console.log('Skipping role refresh - user already has Discord roles');
        return;
      }
      
      refreshUserRoles();
    }
  }, [user?.id, isProcessing, refreshUserRoles, user]); // Include all dependencies

  const handleLogout = () => {
    logout();
    // Navigate immediately - AuthContext will handle the logout state
    navigate('/staff-login');
  };

  // Staff roster management functions
  const loadStaffRoster = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      console.log('Making API call to /api/staff/roster');
      const response = await fetch('/api/staff/roster?refresh=true', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Staff roster data:', data);
      setStaffRoster(data.activeStaff || []);
      setPastStaff(data.pastStaff || []);
    } catch (error) {
      console.error('Error loading staff roster:', error);
      // Fallback to empty arrays if API fails
      setStaffRoster([]);
      setPastStaff([]);
    }
  };

  const handlePanelClick = (panelId: string) => {
    console.log('Panel clicked:', panelId);
    setActivePanel(panelId);
    if (panelId === 'staff-roster') {
      console.log('Loading staff roster...');
      loadStaffRoster();
    } else if (panelId === 'staff-info') {
      console.log('Loading documents...');
      loadDocuments();
    } else if (panelId === 'discord') {
      loadDiscordData();
    }
  };

  const handleBackToDashboard = () => {
    setActivePanel(null);
    setEditingMember(null);
    setShowAddStaffModal(false);
    setShowRemoveStaffModal(false);
    setShowEditPastStaffModal(false);
    setEditingPastMember(null);
    setShowRemovePastStaffModal(false);
    setRemovingPastMember(null);
  };

  // Search guild members
  const searchMembers = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch(`/api/staff/search-members?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again. Try refreshing the page to use cached data.`);
          return;
        }
        
        if (response.status === 500 && errorData.message) {
          alert(`Search error: ${errorData.message}`);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSearchResults(data.members || []);
    } catch (error) {
      console.error('Error searching members:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add role to user
  const addRoleToUser = async (userId: string, roleName: string) => {
    // Prevent setting Founder role through API
    if (roleName === 'Founder') {
      alert('Founder role can only be set manually in Discord.');
      return;
    }
    
    setAddingRoleUserId(userId);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch('/api/staff/add-role', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, roleName })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Refresh the entire roster to get updated data including status
        await loadStaffRoster();
        setShowAddStaffModal(false);
        setSearchQuery('');
        setSearchResults([]);
        alert(data.message);
      }
    } catch (error) {
      console.error('Error adding role:', error);
      alert('Failed to add role. Please check bot permissions.');
    } finally {
      setAddingRoleUserId(null);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear results if query is too short
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    // Debounce search with longer delay to prevent rate limiting
    setTimeout(() => {
      searchMembers(query);
    }, 800);
  };

  // Handle add staff modal
  const handleAddStaff = () => {
    setShowAddStaffModal(!showAddStaffModal);
    if (showAddStaffModal) {
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleEditStaff = (member: StaffMember) => {
    setEditingMember(member);
    setEditingStaffData({
      playfabId: member.playfabId || '',
      recruitmentDate: member.recruitmentDate || '',
      rank: member.rank,
      status: member.status || 'Active'
    });
    setShowEditStaffModal(true);
  };

  const handleSaveStaff = async () => {
    console.log('=== FRONTEND: SAVE STAFF START ===');
    console.log('Editing member:', editingMember);
    console.log('Editing staff data:', editingStaffData);
    
    if (!editingMember) {
      console.log('ERROR: No editing member found');
      return;
    }
    
    setIsSavingStaff(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const requestBody = {
        discordId: editingMember.id,
        playfabId: editingStaffData.playfabId,
        recruitmentDate: editingStaffData.recruitmentDate || null,
        status: editingStaffData.status,
        newRank: editingStaffData.rank !== editingMember.rank ? editingStaffData.rank : null
      };
      
      console.log('Request body being sent:', requestBody);
      console.log('Current rank:', editingMember.rank, 'New rank:', editingStaffData.rank);

      const response = await fetch('/api/staff/update-staff', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        console.log('=== FRONTEND: SAVE STAFF SUCCESS ===');
        // Update the staff roster with new data
        setStaffRoster(prev => prev.map(member => 
          member.id === editingMember.id 
            ? {
                ...member,
                playfabId: editingStaffData.playfabId,
                recruitmentDate: editingStaffData.recruitmentDate || null,
                status: editingStaffData.status,
                rank: editingStaffData.rank !== editingMember.rank ? editingStaffData.rank : member.rank
              }
            : member
        ));
        
        setShowEditStaffModal(false);
        setEditingMember(null);
        setEditingStaffData({ playfabId: '', recruitmentDate: '', rank: 'Admin', status: 'Active' });
      } else {
        console.log('=== FRONTEND: SAVE STAFF ERROR ===');
        console.log('Response status:', response.status);
        const errorData = await response.json().catch(() => ({}));
        console.log('Error data:', errorData);
        
        if (response.status === 403 && errorData.error?.includes('Founder')) {
          alert('Founder role can only be set manually in Discord.');
        } else if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
      } else {
        console.error('Failed to update staff member');
          alert('Failed to update staff member. Please try again.');
        }
      }
    } catch (error) {
      console.log('=== FRONTEND: SAVE STAFF EXCEPTION ===');
      console.error('Error updating staff member:', error);
      alert('Error updating staff member. Please try again.');
    } finally {
      setIsSavingStaff(false);
    }
  };



  const handleRemoveStaffFromEdit = () => {
    setShowRemoveConfirm(true);
  };

  const confirmRemoveFromEdit = async () => {
    if (!editingMember || !removeReason.trim()) return;
    
    setIsRemovingStaff(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      // Remove the role from Discord
      const removeResponse = await fetch('/api/staff/remove-role', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: editingMember.id,
          roleName: editingMember.rank
        })
      });

      if (!removeResponse.ok) {
        const errorData = await removeResponse.json().catch(() => ({}));
        
        if (removeResponse.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
          return;
        }
        
        console.error('Failed to remove staff member');
        return;
      }

      // Add to past staff in database
      const addPastStaffResponse = await fetch('/api/staff/add-past-staff', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          discordId: editingMember.id,
          username: editingMember.username,
          displayName: editingMember.displayName,
          rank: editingMember.rank,
          playfabId: editingMember.playfabId || '',
          recruitmentDate: editingMember.recruitmentDate || '',
          removalReason: removeReason.trim()
        })
      });

      if (!addPastStaffResponse.ok) {
        throw new Error(`Failed to add to past staff: ${addPastStaffResponse.status}`);
      }

      // Clear the backend cache to ensure fresh data
      const clearCacheResponse = await fetch('/api/staff/clear-cache', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

              if (!clearCacheResponse.ok) {
          console.warn('Failed to clear cache, but continuing...');
        }

        // Add a small delay to ensure Discord has processed the role changes
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Reload staff roster to get updated data
        await loadStaffRoster();
      
      // Close all modals
      setShowEditStaffModal(false);
      setShowRemoveConfirm(false);
      setEditingMember(null);
      setEditingStaffData({ playfabId: '', recruitmentDate: '', rank: 'Admin', status: 'Active' });
      setRemoveReason('');
      alert('Staff member removed successfully');
    } catch (error) {
      console.error('Error removing staff member:', error);
      alert('Failed to remove staff member. Please check bot permissions.');
    } finally {
      setIsRemovingStaff(false);
    }
  };



  // const handleRemoveStaff = (member: StaffMember) => {
  //   setRemovingMember(member);
  //   setShowRemoveStaffModal(true);
  // };

  const handleEditPastStaff = (member: PastStaffMember) => {
    setEditingPastMember(member);
    setEditingPastStaffData({
      name: member.name,
      rank: member.rank as 'Founder' | 'Management' | 'Admin',
      playfabID: member.playfabID,
      recruitmentDate: member.recruitmentDate,
      removalReason: member.removalReason
    });
    setShowEditPastStaffModal(true);
  };

  const handleRemovePastStaff = (member: PastStaffMember) => {
    setRemovingPastMember(member);
    setShowRemovePastStaffModal(true);
  };

  const confirmRemoveStaff = async () => {
    if (removingMember && removeReason.trim()) {
      try {
        const token = localStorage.getItem('staffToken');
        if (!token) {
          console.error('No staff token found');
          return;
        }

        // Remove the role from Discord
        const removeResponse = await fetch('/api/staff/remove-role', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: removingMember.id,
            roleName: removingMember.rank
          })
        });

        if (!removeResponse.ok) {
          const errorData = await removeResponse.json().catch(() => ({}));
          
          if (removeResponse.status === 429) {
            const retryAfter = errorData.retryAfter || 60;
            alert(`Discord API rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
            return;
          }
          
          throw new Error(`Failed to remove role: ${removeResponse.status}`);
        }

        // Add to past staff in database
        const addPastStaffResponse = await fetch('/api/staff/add-past-staff', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            discordId: removingMember.id,
            username: removingMember.username,
            displayName: removingMember.displayName,
            rank: removingMember.rank,
            playfabId: removingMember.playfabId || '',
            recruitmentDate: removingMember.recruitmentDate || '',
            removalReason: removeReason.trim()
          })
        });

        if (!addPastStaffResponse.ok) {
          throw new Error(`Failed to add to past staff: ${addPastStaffResponse.status}`);
        }

        // Clear the backend cache to ensure fresh data
        const clearCacheResponse = await fetch('/api/staff/clear-cache', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!clearCacheResponse.ok) {
          console.warn('Failed to clear cache, but continuing...');
        }

        // Add a small delay to ensure Discord has processed the role changes
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Reload staff roster to get updated data
        await loadStaffRoster();
        
        setShowRemoveStaffModal(false);
        setRemovingMember(null);
        setRemoveReason('');
        alert('Staff member removed successfully');
      } catch (error) {
        console.error('Error removing staff member:', error);
        alert('Failed to remove staff member. Please check bot permissions.');
      }
    }
  };

  const handleSavePastStaff = async () => {
    if (!editingPastMember) return;
    
    setIsSavingPastStaff(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch('/api/staff/update-past-staff', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          discordId: editingPastMember.id,
          username: editingPastMember.name, // Using name as username for past staff
          displayName: editingPastStaffData.name,
          rank: editingPastStaffData.rank,
          playfabId: editingPastStaffData.playfabID,
          recruitmentDate: editingPastStaffData.recruitmentDate,
          removalReason: editingPastStaffData.removalReason
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update past staff: ${response.status}`);
      }

      // Reload staff roster to get updated data
      await loadStaffRoster();
      
      setShowEditPastStaffModal(false);
      setEditingPastMember(null);
      setEditingPastStaffData({ name: '', rank: 'Admin', playfabID: '', recruitmentDate: '', removalReason: '' });
      alert('Past staff member updated successfully');
    } catch (error) {
      console.error('Error updating past staff member:', error);
      alert('Failed to update past staff member.');
    } finally {
      setIsSavingPastStaff(false);
    }
  };

  const confirmRemovePastStaff = async () => {
    if (removingPastMember) {
      try {
        const token = localStorage.getItem('staffToken');
        if (!token) {
          console.error('No staff token found');
          return;
        }

        // Remove from past staff in database
        const removeResponse = await fetch('/api/staff/remove-past-staff', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            discordId: removingPastMember.id
          })
        });

        if (!removeResponse.ok) {
          throw new Error(`Failed to remove past staff: ${removeResponse.status}`);
        }

        // Reload staff roster to get updated data
        await loadStaffRoster();
        
        setShowRemovePastStaffModal(false);
        setRemovingPastMember(null);
        alert('Past staff member removed successfully');
      } catch (error) {
        console.error('Error removing past staff member:', error);
        alert('Failed to remove past staff member.');
      }
    }
  };

  const getRankOrder = (rank: string): number => {
    switch (rank) {
      case 'Founder': return 1;
      case 'Management': return 2;
      case 'Admin': return 3;
      default: return 4;
    }
  };

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'Founder': return 'text-purple-400';
      case 'Management': return 'text-blue-400';
      case 'Admin': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'Founder': return 'text-purple-400';
      case 'Management': return 'text-blue-400';
      case 'Admin': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500', 
      'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-orange-500'
    ];
    const index = category.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Export PlayFab IDs functions
  const exportAllPlayFabIds = () => {
    const playfabIds = staffRoster
      .filter(member => member.playfabId && member.playfabId.trim() !== '')
      .map(member => member.playfabId.trim());
    
    if (playfabIds.length === 0) {
      alert('No PlayFab IDs found in the roster.');
      return;
    }
    
    const content = playfabIds.join('\n');
    downloadTextFile(content, 'all_playfab_ids.txt');
  };

  const exportManagementPlayFabIds = () => {
    const playfabIds = staffRoster
      .filter(member => 
        (member.rank === 'Founder' || member.rank === 'Management') && 
        member.playfabId && 
        member.playfabId.trim() !== ''
      )
      .map(member => member.playfabId.trim());
    
    if (playfabIds.length === 0) {
      alert('No PlayFab IDs found for Management+ staff.');
      return;
    }
    
    const content = playfabIds.join('\n');
    downloadTextFile(content, 'management_playfab_ids.txt');
  };

  // Sync admin list with in-game admin list
  const syncAdminList = async () => {
    if (!selectedServer) {
      alert('Please select a server first');
      return;
    }

    if (selectedServer.queryType !== 'rcon') {
      alert('This server does not support RCON commands');
      return;
    }

    if (!window.confirm('This will sync the staff roster with the in-game admin list. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/sync-adminlist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Admin list sync completed!\n\n${data.message}\n\nAdded: ${data.details.adminsToAdd.length}\nRemoved: ${data.details.adminsToRemove.length}`);
        
        // Show detailed results in console for debugging
        console.log('Admin sync details:', data.details);
      } else {
        const errorData = await response.json();
        alert(`Error syncing admin list: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error syncing admin list:', error);
      alert(`Error syncing admin list: ${error instanceof Error ? error.message : 'Network error'}`);
    }
  };

  const downloadTextFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Discord Stats functions
  const loadDiscordData = async () => {
    setIsLoadingDiscordData(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }
      const [statsResponse, activityResponse, logsResponse] = await Promise.all([
        fetch('/api/discord-stats/server-stats', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch('/api/discord-stats/recent-activity', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch('/api/discord-stats/moderation-logs', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);
      


      if (statsResponse.ok && activityResponse.ok && logsResponse.ok) {
        const statsData = await statsResponse.json();
        const activityData = await activityResponse.json();
        const logsData = await logsResponse.json();
        
        setDiscordStats(statsData);
        setRecentActivity(activityData);
        setModerationLogs(logsData);
      } else {
        console.error('Failed to fetch Discord data');
        if (!statsResponse.ok) {
          const errorData = await statsResponse.json().catch(() => ({}));
          console.error('Server stats error:', errorData);
        }
        if (!activityResponse.ok) {
          const errorData = await activityResponse.json().catch(() => ({}));
          console.error('Activity error:', errorData);
        }
        if (!logsResponse.ok) {
          const errorData = await logsResponse.json().catch(() => ({}));
          console.error('Logs error:', errorData);
        }
      }
    } catch (error) {
      console.error('Error loading Discord data:', error);
    } finally {
      setIsLoadingDiscordData(false);
    }
  };

  // Staff Info functions
  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      console.log('Making API calls to staff-documents');
      const [documentsResponse, categoriesResponse] = await Promise.all([
        fetch('/api/staff-documents', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch('/api/staff-documents/categories/list', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      console.log('Documents response status:', documentsResponse.status);
      console.log('Categories response status:', categoriesResponse.status);

      if (documentsResponse.ok && categoriesResponse.ok) {
        const documentsData = await documentsResponse.json();
        const categoriesData = await categoriesResponse.json();
        console.log('Documents data:', documentsData);
        console.log('Categories data:', categoriesData);
        setDocuments(documentsData);
        setCategories(categoriesData);
      } else {
        console.error('Failed to fetch documents or categories');
        if (!documentsResponse.ok) {
          const errorData = await documentsResponse.json().catch(() => ({}));
          console.error('Documents API Error:', errorData);
        }
        if (!categoriesResponse.ok) {
          const errorData = await categoriesResponse.json().catch(() => ({}));
          console.error('Categories API Error:', errorData);
        }
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  // Load game servers
  const loadGameServers = async (checkStatus = false) => {
    if (checkStatus) {
      setIsRefreshingAll(true);
      setRefreshFeedback('Checking server statuses...');
    }
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const url = checkStatus ? '/api/logs/servers?checkStatus=true' : '/api/logs/servers';
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const updatedServers = data.servers || [];
        setGameServers(updatedServers);
        
        // Update selected server if it exists in the updated list
        if (selectedServer && updatedServers.length > 0) {
          const updatedSelectedServer = updatedServers.find((s: GameServer) => s.id === selectedServer.id);
          if (updatedSelectedServer) {
            setSelectedServer(updatedSelectedServer);
          }
        } else if (updatedServers.length > 0 && !selectedServer) {
          const firstServer = updatedServers[0];
          setSelectedServer(firstServer);
          // Also load webhooks and logs for the auto-selected server
          loadWebhooks(firstServer.id);
          loadLogs(firstServer.id, { ...logFilters, type: 'all' }, 1);
        }
        
        if (checkStatus) {
          setRefreshFeedback('Status check completed!');
          setTimeout(() => setRefreshFeedback(''), 2000);
        }
      } else {
        console.error('Failed to load game servers');
        if (checkStatus) {
          setRefreshFeedback('Failed to check status');
          setTimeout(() => setRefreshFeedback(''), 3000);
        }
      }
    } catch (error) {
      console.error('Error loading game servers:', error);
      if (checkStatus) {
        setRefreshFeedback('Error checking status');
        setTimeout(() => setRefreshFeedback(''), 3000);
      }
    } finally {
      if (checkStatus) {
        setIsRefreshingAll(false);
      }
    }
  };

  // Check specific server status
  const checkServerStatus = async (serverId: number) => {
    setIsCheckingStatus(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${serverId}/check-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Reload servers to get updated status
        await loadGameServers();
        alert(`Server status updated: ${data.status.toUpperCase()}`);
      } else {
        console.error('Failed to check server status');
        alert('Failed to check server status');
      }
    } catch (error) {
      console.error('Error checking server status:', error);
      alert('Error checking server status');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Load webhooks for selected server
  const loadWebhooks = async (serverId: number) => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${serverId}/webhooks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks || []);
      } else {
        console.error('Failed to load webhooks');
      }
    } catch (error) {
      console.error('Error loading webhooks:', error);
    }
  };

  // Load logs for selected server
  const loadLogs = async (serverId: number, filters = {}, page = 1) => {
    setIsLoadingLogs(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        setLogs([]); // Ensure logs is always an array
        return;
      }

      const queryParams = new URLSearchParams({
        ...filters,
        server_id: serverId.toString(),
        page: page.toString(),
        limit: '50'
      });
      const response = await fetch(`/api/logs/logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        
        // Update pagination state
        if (data.pagination) {
          setCurrentPage(data.pagination.page);
          setTotalPages(data.pagination.totalPages);
          setTotalLogs(data.pagination.total);
        }
      } else {
        console.error('Failed to load logs');
        setLogs([]); // Ensure logs is always an array
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]); // Ensure logs is always an array on error
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Handle server selection
  const handleServerSelect = (server: GameServer) => {
    setSelectedServer(server);
    loadWebhooks(server.id);
    // Load "all" logs by default when selecting a server
    loadLogs(server.id, { ...logFilters, type: 'all' }, 1);
  };

  // Handle server form changes
  const handleServerFormChange = (field: string, value: string | number | 'gamedig' | 'source' | 'rcon') => {
    setServerForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle webhook form changes
  const handleWebhookFormChange = (field: string, value: string) => {
    setWebhookForm(prev => ({ ...prev, [field]: value }));
  };

  // Add new server
  const handleAddServer = () => {
    setServerForm({
      name: '',
      game: '',
      ip: '',
      port: 25565,
      queryType: 'gamedig',
      rconPassword: '',
      rconMaxPlayers: 100,
      description: ''
    });
    setShowAddServerModal(true);
  };

  // Edit server
  const handleEditServer = (server: GameServer) => {
    setEditingServer(server);
    setServerForm({
      name: server.name,
      game: server.game,
      ip: server.ip,
      port: server.port,
      queryType: server.queryType,
      rconPassword: server.rconPassword || '',
      rconMaxPlayers: server.rconMaxPlayers || 100,
      description: server.description
    });
    setShowEditServerModal(true);
  };

  // Delete server
  const handleDeleteServer = (server: GameServer) => {
    setEditingServer(server);
    setShowDeleteServerModal(true);
  };

  // Save server
  const handleSaveServer = async () => {
    setIsSavingServer(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const url = editingServer 
        ? `/api/logs/servers/${editingServer.id}`
        : '/api/logs/servers';
      
      const method = editingServer ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(serverForm),
      });

      if (response.ok) {
        setShowAddServerModal(false);
        setShowEditServerModal(false);
        setEditingServer(null);
        
        // If we're editing a server, update the selectedServer with the new data
        if (editingServer && selectedServer && editingServer.id === selectedServer.id) {
          const updatedServer = {
            ...editingServer,
            ...serverForm
          };
          setSelectedServer(updatedServer);
        }
        
        loadGameServers();
      } else {
        console.error('Failed to save server');
      }
    } catch (error) {
      console.error('Error saving server:', error);
    } finally {
      setIsSavingServer(false);
    }
  };

  // Confirm delete server
  const confirmDeleteServer = async () => {
    if (!editingServer) return;
    
    setIsSavingServer(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${editingServer.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowDeleteServerModal(false);
        setEditingServer(null);
        setSelectedServer(null);
        loadGameServers();
        alert('Server deleted successfully');
      } else {
        console.error('Failed to delete server');
        alert('Failed to delete server');
      }
    } catch (error) {
      console.error('Error deleting server:', error);
      alert('Error deleting server');
    } finally {
      setIsSavingServer(false);
    }
  };

  // Add webhook
  const handleAddWebhook = () => {
    if (!selectedServer) return;
    
    setWebhookForm({
      name: '',
      type: '',
      emoji: '',
      url: ''
    });
    setShowAddWebhookModal(true);
  };

  // Edit webhook
  const handleEditWebhook = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setWebhookForm({
      name: webhook.name,
      type: webhook.type,
      emoji: webhook.emoji,
      url: webhook.url
    });
    setShowEditWebhookModal(true);
  };

  // Save webhook
  const handleSaveWebhook = async () => {
    if (!selectedServer) return;
    
    setIsSavingWebhook(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const url = editingWebhook 
        ? `/api/logs/webhooks/${editingWebhook.id}`
        : `/api/logs/servers/${selectedServer.id}/webhooks`;
      
      const method = editingWebhook ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookForm),
      });

      if (response.ok) {
        setShowAddWebhookModal(false);
        setShowEditWebhookModal(false);
        setEditingWebhook(null);
        loadWebhooks(selectedServer.id);
      } else {
        console.error('Failed to save webhook');
      }
    } catch (error) {
      console.error('Error saving webhook:', error);
    } finally {
      setIsSavingWebhook(false);
    }
  };

  // Handle log filters
  const handleLogFilterChange = (field: string, value: string) => {
    const newFilters = { ...logFilters, [field]: value };
    setLogFilters(newFilters);
    // Reset pagination when filters change
    setCurrentPage(1);
    // Ensure logs is always an array when changing filters
    setLogs([]);
    if (selectedServer) {
      // Include the current active log tab type in the filters
      const filtersWithType = { 
        ...newFilters, 
        type: activeLogTab === 'all' ? 'all' : activeLogTab 
      };
      loadLogs(selectedServer.id, filtersWithType, 1);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (selectedServer) {
      // Include the current active log tab type in the filters
      const filtersWithType = { 
        ...logFilters, 
        type: activeLogTab === 'all' ? 'all' : activeLogTab 
      };
      loadLogs(selectedServer.id, filtersWithType, page);
    }
  };

  // Handle refresh logs
  const handleRefreshLogs = () => {
    if (selectedServer) {
      // Preserve the current log tab when refreshing
      const filtersWithType = { 
        ...logFilters, 
        type: activeLogTab === 'all' ? 'all' : activeLogTab 
      };
      loadLogs(selectedServer.id, filtersWithType, currentPage);
    }
  };

  // Handle log tab change
  const handleLogTabChange = (tab: string) => {
    setActiveLogTab(tab);
    
    // Reset pagination when changing tabs
    setCurrentPage(1);
    
    // Ensure logs is always an array when switching tabs
    setLogs([]);
    
    // Load logs for the selected tab
    if (selectedServer) {
      loadLogs(selectedServer.id, { ...logFilters, type: tab === 'all' ? 'all' : tab }, 1);
    }
  };

  // Load data when panel changes
  useEffect(() => {
    if (activePanel === 'staff-info') {
      loadDocuments();
    } else if (activePanel === 'logs') {
      loadGameServers(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel]);

  // Auto-select first server and load logs when gameServers are loaded and no server is selected
  useEffect(() => {
    console.log('Auto-select useEffect triggered:', { 
      activePanel, 
      gameServersLength: gameServers.length, 
      selectedServer: !!selectedServer 
    });
    
    if (activePanel === 'logs' && gameServers.length > 0 && !selectedServer) {
      console.log('Auto-selecting first server and loading logs');
      const firstServer = gameServers[0];
      console.log('First server:', firstServer);
      setSelectedServer(firstServer);
      loadWebhooks(firstServer.id);
      loadLogs(firstServer.id, { ...logFilters, type: 'all' }, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameServers, activePanel, selectedServer]);



  const handleCreateDocument = () => {
    setDocumentForm({
      title: '',
      content: '',
      category: 'general',
      access_level: 'Admin'
    });
    setShowCreateDocumentModal(true);
  };

  const handleEditDocument = (document: any) => {
    setSelectedDocument(document);
    setDocumentForm({
      title: document.title,
      content: document.content,
      category: document.category,
      access_level: document.access_level
    });
    setShowEditDocumentModal(true);
  };

  const handleViewDocument = (document: any) => {
    setSelectedDocument(document);
    setShowViewDocumentModal(true);
  };

  const handleDeleteDocument = (document: any) => {
    setDeletingDocument(document);
    setShowDeleteDocumentModal(true);
  };

  const handleSaveDocument = async () => {
    if (!documentForm.title.trim() || !documentForm.content.trim()) {
      alert('Title and content are required');
      return;
    }

    setIsSavingDocument(true);
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const url = showEditDocumentModal 
        ? `/api/staff-documents/${selectedDocument.id}`
        : '/api/staff-documents';
      
      const method = showEditDocumentModal ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(documentForm)
      });

      if (response.ok) {
        await loadDocuments();
        setShowCreateDocumentModal(false);
        setShowEditDocumentModal(false);
        setSelectedDocument(null);
        setDocumentForm({ title: '', content: '', category: 'general', access_level: 'Admin' });
        alert(showEditDocumentModal ? 'Document updated successfully' : 'Document created successfully');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to save document');
      }
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Failed to save document');
    } finally {
      setIsSavingDocument(false);
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deletingDocument) return;

    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No staff token found');
        return;
      }

      const response = await fetch(`/api/staff-documents/${deletingDocument.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadDocuments();
        setShowDeleteDocumentModal(false);
        setDeletingDocument(null);
        alert('Document deleted successfully');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  // Quick Actions handlers
  const handleQuickAction = async () => {
    if (!selectedServer || !quickAction || !targetPlayFabId) return;
    
    setIsExecutingAction(true);
    setActionOutput('Executing action...\n');
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        setActionOutput('Error: No authentication token found');
        return;
      }

      console.log('Making quick action request with token:', token.substring(0, 20) + '...');
      console.log('Request body:', {
        action: quickAction,
        targetPlayFabId,
        targetPlayerName,
        reason,
        duration: duration ? parseInt(duration) : 0
      });

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/quick-actions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: quickAction,
          targetPlayFabId,
          targetPlayerName,
          reason,
          duration: duration ? parseInt(duration) : 0
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const data = await response.json();
        setActionOutput(data.output || data.message || 'Action executed successfully');
        
        // Clear form after successful execution
        setQuickAction('');
        setTargetPlayFabId('');
        setTargetPlayerName('');
        setReason('');
        setDuration('');
      } else {
        // Try to get the response text first to see what we're actually getting
        const responseText = await response.text();
        console.log('Error response text:', responseText);
        
        try {
          const errorData = JSON.parse(responseText);
          setActionOutput(`Error: ${errorData.error || 'Failed to execute action'}`);
        } catch (parseError) {
          setActionOutput(`Error: Received non-JSON response (${response.status}): ${responseText.substring(0, 200)}...`);
        }
      }
    } catch (error) {
      console.error('Quick action error:', error);
      setActionOutput(`Error: ${error instanceof Error ? error.message : 'Network error'}`);
    } finally {
      setIsExecutingAction(false);
    }
  };

  // Player search handler
  const handlePlayerSearch = async (query: string) => {
    if (!selectedServer || !query.trim() || query.length < 2) {
      setPlayerSearchResults([]);
      return;
    }
    
    setIsSearchingPlayers(true);
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      console.log('Making player search request with token:', token.substring(0, 20) + '...');
      console.log('Search query:', query);

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/search-players?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Search response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Search results:', data);
        setPlayerSearchResults(data.players || []);
      } else {
        // Try to get the response text first to see what we're actually getting
        const responseText = await response.text();
        console.error('Search failed. Response text:', responseText);
        setPlayerSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching players:', error);
      setPlayerSearchResults([]);
    } finally {
      setIsSearchingPlayers(false);
    }
  };

  // Lookup player search handler (separate from Quick Actions search)
  const handleLookupPlayerSearch = async (query: string) => {
    if (!selectedServer || !query.trim() || query.length < 2) {
      setLookupSearchResults([]);
      return;
    }
    
    setIsSearchingLookup(true);
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/search-players?query=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLookupSearchResults(data.players || []);
      } else {
        const responseText = await response.text();
        console.error('Lookup search failed. Response text:', responseText);
        setLookupSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching lookup players:', error);
      setLookupSearchResults([]);
    } finally {
      setIsSearchingLookup(false);
    }
  };

  // Player lookup handler
  const handlePlayerLookup = async () => {
    if (!selectedServer || !lookupPlayFabId.trim()) return;
    
    setIsLookingUpPlayer(true);
    setLookupOutput('Looking up player...\n');
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        setLookupOutput('Error: No authentication token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/lookup-player`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          playFabId: lookupPlayFabId.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLookupOutput(data.output || 'Player lookup completed');
      } else {
        const errorData = await response.json();
        setLookupOutput(`Error: ${errorData.error || 'Failed to lookup player'}`);
      }
    } catch (error) {
      setLookupOutput(`Error: ${error instanceof Error ? error.message : 'Network error'}`);
    } finally {
      setIsLookingUpPlayer(false);
    }
  };

  // Select player from search results
  const selectPlayer = (player: any) => {
    setTargetPlayFabId(player.player_id);
    setTargetPlayerName(player.player_name);
    setPlayerSearchResults([]);
  };



  // RCON Command handler
  const handleRconCommand = async () => {
    if (!selectedServer || !rconCommand.trim()) return;
    
    // Check if command starts with 'listen' (case insensitive)
    if (rconCommand.trim().toLowerCase().startsWith('listen')) {
      setRconOutput('Error: The "listen" command is not allowed for security reasons.');
      return;
    }
    
    setIsExecutingRcon(true);
    setRconOutput('Executing command...\n');
    
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        setRconOutput('Error: No authentication token found');
        return;
      }

      const response = await fetch(`/api/logs/servers/${selectedServer.id}/rcon`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          command: rconCommand.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRconOutput(data.output || 'Command executed successfully');
      } else {
        const errorData = await response.json();
        setRconOutput(`Error: ${errorData.error || 'Failed to execute command'}`);
      }
    } catch (error) {
      setRconOutput(`Error: ${error instanceof Error ? error.message : 'Network error'}`);
    } finally {
      setIsExecutingRcon(false);
      setRconCommand(''); // Clear the command input after execution
    }
  };







  const filteredDocuments = documents.filter(doc => 
    selectedCategory === 'all' || doc.category === selectedCategory
  );

  const sortedStaffRoster = [...staffRoster].sort((a, b) => {
    // First sort by rank
    const rankComparison = getRankOrder(a.rank) - getRankOrder(b.rank);
    if (rankComparison !== 0) {
      return rankComparison;
    }
    // If ranks are the same, sort alphabetically by display name
    return a.displayName.localeCompare(b.displayName);
  });

  if (loading || isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto"></div>
          <p className="text-gray-300 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to staff-login
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <div className="glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold gradient-text">
                {activePanel ? 
                  activePanel === 'logs' ? 'Servers & Logs' :
                  `${activePanel.charAt(0).toUpperCase() + activePanel.slice(1).replace('-', ' ').replace('roster', 'Roster').replace('info', 'Information')}` 
                  : (window.location.pathname === '/staff' ? 'Staff Dashboard' : 'Admin Dashboard')}
              </h1>
              <p className="text-gray-300 text-sm">
                Welcome, {user.username} • {getUserRoleName(user)}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {activePanel && (
                <button
                  onClick={handleBackToDashboard}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
                >
                  Back to Dashboard
                </button>
              )}
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
            >
              Logout
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activePanel === 'staff-roster' ? (
          <StaffRosterPanel 
            staffRoster={sortedStaffRoster}
            pastStaff={pastStaff}
            canEdit={hasPermission(2)} // Management level and up
            onEditStaff={handleEditStaff}
            onAddStaff={handleAddStaff}
            onEditPastStaff={handleEditPastStaff}
            onRemovePastStaff={handleRemovePastStaff}
            onRefreshRoster={loadStaffRoster}
            searchQuery={searchQuery}
            searchResults={searchResults}
            isSearching={isSearching}
            selectedRole={selectedRole}
            isAddingRole={addingRoleUserId !== null}
            onSearchChange={handleSearchChange}
            onAddRole={addRoleToUser}
            onRoleChange={setSelectedRole}
            showAddStaffModal={showAddStaffModal}
            onExportAllPlayFabIds={exportAllPlayFabIds}
            onExportManagementPlayFabIds={exportManagementPlayFabIds}
            exportDropdownRef={exportDropdownRef}
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            onSyncAdminList={syncAdminList}
          />

        ) : activePanel === 'staff-info' ? (
          <StaffInfoPanel
            documents={filteredDocuments}
            categories={categories}
            selectedCategory={selectedCategory}
            canEdit={hasPermission(2)} // Management level and up
            onCreateDocument={handleCreateDocument}
            onEditDocument={handleEditDocument}
            onViewDocument={handleViewDocument}
            onDeleteDocument={handleDeleteDocument}
            onCategoryChange={setSelectedCategory}
            onRefreshDocuments={loadDocuments}
            getCategoryColor={getCategoryColor}
            getAccessLevelColor={getAccessLevelColor}
          />

        ) : activePanel === 'discord' ? (
          <div className="glass p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Discord Moderation</h2>
                <p className="text-gray-300">Review logs and moderate the Discord server</p>
              </div>
              {isLoadingDiscordData && (
                <div className="flex items-center text-cyan-400">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400 mr-2"></div>
                  Loading...
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Recent Activity */}
              <div className="glass p-4 rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-3">Recent Activity</h3>
                <div className="space-y-2">
                  {isLoadingDiscordData ? (
                    <div className="text-sm text-gray-400">Loading activity...</div>
                  ) : recentActivity.length > 0 ? (
                    recentActivity.slice(0, 3).map((activity) => (
                      <div key={activity.id} className="text-sm text-gray-300">
                        <span className="text-cyan-400">[{new Date(activity.timestamp).toLocaleTimeString()}]</span> {activity.details}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-400">No recent activity</div>
                  )}
                </div>
              </div>

              {/* Moderation Actions */}
              <div className="glass p-4 rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <button className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors">
                    Ban User
                  </button>
                  <button className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm transition-colors">
                    Timeout User
                  </button>
                  <button className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors">
                    View Logs
                  </button>
                </div>
              </div>

              {/* Server Stats */}
              <div className="glass p-4 rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-3">Server Stats</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  {isLoadingDiscordData ? (
                    <div className="text-gray-400">Loading stats...</div>
                  ) : discordStats ? (
                    <>
                      <div>Online Members: <span className="text-green-400">{discordStats.onlineMembers}</span></div>
                      <div>Total Members: <span className="text-blue-400">{discordStats.totalMembers}</span></div>
                      <div>Active Channels: <span className="text-purple-400">{discordStats.activeChannels}</span></div>
                      {discordStats.boostLevel > 0 && (
                        <div>Boost Level: <span className="text-pink-400">Level {discordStats.boostLevel}</span></div>
                      )}
                    </>
                  ) : (
                    <div className="text-gray-400">
                      Failed to load stats
                      <div className="text-xs text-gray-500 mt-1">
                        Check Discord bot configuration
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Moderation Logs</h3>
              <div className="glass border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-gray-800 px-4 py-3 border-b border-gray-600">
                  <div className="grid grid-cols-5 gap-4 text-sm font-medium text-gray-300">
                    <div>Time</div>
                    <div>Action</div>
                    <div>Moderator</div>
                    <div>User</div>
                    <div>Reason</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-600">
                  {isLoadingDiscordData ? (
                    <div className="px-4 py-3 text-center text-gray-400">Loading logs...</div>
                  ) : moderationLogs.length > 0 ? (
                    moderationLogs.slice(0, 5).map((log) => (
                      <div key={log.id} className="px-4 py-3 grid grid-cols-5 gap-4 text-sm">
                        <div className="text-gray-300">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                        <div className={`${
                          log.action === 'ban' ? 'text-red-400' :
                          log.action === 'timeout' ? 'text-yellow-400' :
                          log.action === 'warning' ? 'text-green-400' :
                          log.action === 'kick' ? 'text-orange-400' :
                          'text-gray-400'
                        } capitalize`}>
                          {log.action}
                        </div>
                        <div className="text-white">{log.moderator}</div>
                        <div className="text-white">{log.user}</div>
                        <div className="text-gray-400">{log.reason}</div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-gray-400">No moderation logs found</div>
                  )}
                </div>
              </div>
            </div>
          </div>

        ) : activePanel === 'logs' ? (
                      <LogsPanel
              gameServers={gameServers}
              selectedServer={selectedServer}
              webhooks={webhooks}
              logs={logs}
              canEdit={hasPermission(2)} // Management level and up
              onServerSelect={handleServerSelect}
              onAddServer={handleAddServer}
              onEditServer={handleEditServer}
              onDeleteServer={handleDeleteServer}
              onAddWebhook={handleAddWebhook}
              onEditWebhook={handleEditWebhook}
              onLogFilterChange={handleLogFilterChange}
              onLogTabChange={handleLogTabChange}
              activeLogTab={activeLogTab}
              logFilters={logFilters}
              isLoadingLogs={isLoadingLogs}
              onCheckServerStatus={checkServerStatus}
              onRefreshAllServers={loadGameServers}
              isCheckingStatus={isCheckingStatus}
              isRefreshingAll={isRefreshingAll}
              refreshFeedback={refreshFeedback}
              hasPermission={hasPermission}
              currentPage={currentPage}
              totalPages={totalPages}
              totalLogs={totalLogs}
              handlePageChange={handlePageChange}
              onRefreshLogs={handleRefreshLogs}
              // Quick Actions props
              quickAction={quickAction}
              setQuickAction={setQuickAction}
              targetPlayFabId={targetPlayFabId}
              setTargetPlayFabId={setTargetPlayFabId}
              targetPlayerName={targetPlayerName}
              setTargetPlayerName={setTargetPlayerName}
              reason={reason}
              setReason={setReason}
              duration={duration}
              setDuration={setDuration}
              isExecutingAction={isExecutingAction}
              actionOutput={actionOutput}
              setActionOutput={setActionOutput}
              handleQuickAction={handleQuickAction}
              // Player search props
              playerSearchResults={playerSearchResults}
              isSearchingPlayers={isSearchingPlayers}
              handlePlayerSearch={handlePlayerSearch}
              selectPlayer={selectPlayer}
              setPlayerSearchResults={setPlayerSearchResults}
              // Lookup search props
              lookupSearchResults={lookupSearchResults}
              isSearchingLookup={isSearchingLookup}
              handleLookupPlayerSearch={handleLookupPlayerSearch}
              setLookupSearchResults={setLookupSearchResults}
              // Player lookup props
              lookupPlayFabId={lookupPlayFabId}
              setLookupPlayFabId={setLookupPlayFabId}
              lookupOutput={lookupOutput}
              setLookupOutput={setLookupOutput}
              isLookingUpPlayer={isLookingUpPlayer}
              handlePlayerLookup={handlePlayerLookup}
              // RCON Command Line props
              rconCommand={rconCommand}
              setRconCommand={setRconCommand}
              rconOutput={rconOutput}
              setRconOutput={setRconOutput}
              isExecutingRcon={isExecutingRcon}
              handleRconCommand={handleRconCommand}

            />

        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {DASHBOARD_SECTIONS.map((section) => {
            // Only show sections the user has permission to access
            if (!hasPermission(section.requiredLevel)) {
              return null;
            }

            return (
              <div key={section.id} className="glass p-6 rounded-xl border border-white/10">
                <div className="flex items-center mb-4">
                  <div className={`w-10 h-10 ${section.bgColor} rounded-lg flex items-center justify-center mr-3`}>
                    {section.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                </div>
                <p className="text-gray-300 text-sm mb-4">{section.description}</p>
                  <button 
                    onClick={() => handlePanelClick(section.id)}
                    className={`w-full ${section.buttonColor} text-white py-2 px-4 rounded-lg transition-colors duration-200`}
                  >
                  {section.buttonText}
                </button>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Remove Staff Modal */}
      {showRemoveStaffModal && removingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Remove Staff Member</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to remove <span className="text-cyan-300">{removingMember.displayName}</span> from the staff roster?
            </p>
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Removal Reason *</label>
              <textarea
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
                placeholder="Enter the reason for removal..."
                rows={3}
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRemoveStaffModal(false);
                  setRemovingMember(null);
                  setRemoveReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveStaff}
                disabled={!removeReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Past Staff Modal */}
      {showRemovePastStaffModal && removingPastMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Delete Past Staff Member</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to permanently delete <span className="text-cyan-300">{removingPastMember.name}</span> from the past staff records?
            </p>
            <p className="text-red-400 text-sm mb-4">
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRemovePastStaffModal(false);
                  setRemovingPastMember(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemovePastStaff}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Document Modal */}
      {(showCreateDocumentModal || showEditDocumentModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">
              {showCreateDocumentModal ? 'Create New Document' : 'Edit Document'}
            </h3>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Title *</label>
                <input
                  type="text"
                  value={documentForm.title}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter document title..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Category</label>
                <input
                  type="text"
                  value={documentForm.category}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Enter category (e.g., guidelines, procedures, policies)..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Access Level */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Access Level</label>
                <select
                  value={documentForm.access_level}
                  onChange={(e) => setDocumentForm(prev => ({ ...prev, access_level: e.target.value as 'Admin' | 'Management' | 'Founder' }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="Admin">Admin</option>
                  <option value="Management">Management</option>
                  <option value="Founder">Founder</option>
                </select>
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Content *</label>
                <RichTextEditor
                  value={documentForm.content}
                  onChange={(content) => setDocumentForm(prev => ({ ...prev, content }))}
                  placeholder="Start writing your document..."
                  className="min-h-[300px]"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateDocumentModal(false);
                  setShowEditDocumentModal(false);
                  setSelectedDocument(null);
                  setDocumentForm({ title: '', content: '', category: 'general', access_level: 'Admin' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDocument}
                disabled={isSavingDocument || !documentForm.title.trim() || !documentForm.content.trim()}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isSavingDocument ? 'Saving...' : (showCreateDocumentModal ? 'Create Document' : 'Update Document')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showViewDocumentModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-white">{selectedDocument.title}</h3>
              <button
                onClick={() => {
                  setShowViewDocumentModal(false);
                  setSelectedDocument(null);
                }}
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4 flex items-center gap-4 text-sm text-gray-300">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedDocument.category)} text-white`}>
                {selectedDocument.category}
              </span>
              <span className={`text-xs font-medium ${getAccessLevelColor(selectedDocument.access_level)}`}>
                {selectedDocument.access_level}
              </span>
              <span>By {selectedDocument.author_name}</span>
              <span>{new Date(selectedDocument.updated_at).toLocaleDateString()}</span>
            </div>
            
            <div 
              className="prose prose-invert max-w-none text-gray-300"
              dangerouslySetInnerHTML={{ __html: selectedDocument.content }}
            />
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowViewDocumentModal(false);
                  setSelectedDocument(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Document Modal */}
      {showDeleteDocumentModal && deletingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Delete Document</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to permanently delete <span className="text-cyan-300">{deletingDocument.title}</span>?
            </p>
            <p className="text-red-400 text-sm mb-4">
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDocumentModal(false);
                  setDeletingDocument(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDocument}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff Member Modal */}
      {showAddStaffModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Add Staff Member</h3>
            
            {/* Search Input */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Search Guild Members</label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by username, display name, or ID..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Select Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as 'Management' | 'Admin')}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Admin">Admin</option>
                <option value="Management">Management</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Founder role can only be set manually in Discord</p>
            </div>

            {/* Search Results */}
            {isSearching && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400 mx-auto"></div>
                <p className="text-gray-300 mt-2">Searching...</p>
              </div>
            )}

            {!isSearching && searchResults.length > 0 && (
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-2">Select User</label>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {searchResults.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 bg-gray-800 rounded-lg hover:bg-gray-700 cursor-pointer"
                      onClick={() => addRoleToUser(member.id, selectedRole)}
                    >
                      <div className="flex items-center">
                        {member.avatar && (
                          <img 
                            src={member.avatar} 
                            alt={member.displayName} 
                            className="w-8 h-8 rounded-full mr-3"
                          />
                        )}
                        <div>
                          <p className="text-white font-medium">{member.displayName}</p>
                          <p className="text-gray-400 text-sm">@{member.username}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addRoleToUser(member.id, selectedRole);
                        }}
                        disabled={addingRoleUserId === member.id}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50"
                      >
                        {addingRoleUserId === member.id ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-400">No users found matching "{searchQuery}"</p>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleAddStaff}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Member Modal */}
      {showEditStaffModal && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Edit Staff Member</h3>
            
            <div className="mb-4">
              <div className="flex items-center mb-3">
                {editingMember.avatar && (
                  <img 
                    src={editingMember.avatar} 
                    alt={editingMember.displayName} 
                    className="w-10 h-10 rounded-full mr-3"
                  />
                )}
                <div>
                  <p className="text-white font-medium">{editingMember.displayName}</p>
                  <p className="text-gray-400 text-sm">@{editingMember.username}</p>
                  <p className={`text-sm ${getRankColor(editingMember.rank)}`}>{editingMember.rank}</p>
                </div>
              </div>
            </div>

            {/* PlayFab ID */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">PlayFab ID</label>
              <input
                type="text"
                value={editingStaffData.playfabId}
                onChange={(e) => setEditingStaffData(prev => ({ ...prev, playfabId: e.target.value }))}
                placeholder="Enter PlayFab ID..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>



            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Role</label>
              <select
                value={editingStaffData.rank}
                onChange={(e) => setEditingStaffData(prev => ({ ...prev, rank: e.target.value as 'Management' | 'Admin' }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Admin">Admin</option>
                <option value="Management">Management</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Founder role can only be set manually in Discord</p>
            </div>

            {/* Status Selection */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Status</label>
              <select
                value={editingStaffData.status}
                onChange={(e) => setEditingStaffData(prev => ({ ...prev, status: e.target.value as 'Active' | 'Exempt' | 'Inactive' | 'On Leave' }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Active">Active</option>
                <option value="Exempt">Exempt</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
              </select>
            </div>

            {/* Recruitment Date */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm mb-2">Recruitment Date</label>
              <input
                type="date"
                value={editingStaffData.recruitmentDate}
                onChange={(e) => setEditingStaffData(prev => ({ ...prev, recruitmentDate: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowEditStaffModal(false);
                    setEditingMember(null);
                    setEditingStaffData({ playfabId: '', recruitmentDate: '', rank: 'Admin', status: 'Active' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStaff}
                  disabled={isSavingStaff}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                >
                  {isSavingStaff ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              
              {/* Remove Action */}
              <div className="flex space-x-3">
                <button
                  onClick={handleRemoveStaffFromEdit}
                  disabled={isRemovingStaff}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                >
                  Remove Staff
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Past Staff Modal */}
      {showEditPastStaffModal && editingPastMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Edit Past Staff Member</h3>
            
            <div className="mb-4">
              <div className="flex items-center mb-3">
                <div>
                  <p className="text-white font-medium">{editingPastMember.name}</p>
                  <p className={`text-sm ${getRankColor(editingPastMember.rank)}`}>{editingPastMember.rank}</p>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Display Name</label>
              <input
                type="text"
                value={editingPastStaffData.name}
                onChange={(e) => setEditingPastStaffData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter display name..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* PlayFab ID */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">PlayFab ID</label>
              <input
                type="text"
                value={editingPastStaffData.playfabID}
                onChange={(e) => setEditingPastStaffData(prev => ({ ...prev, playfabID: e.target.value }))}
                placeholder="Enter PlayFab ID..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Role</label>
              <select
                value={editingPastStaffData.rank}
                onChange={(e) => setEditingPastStaffData(prev => ({ ...prev, rank: e.target.value as 'Founder' | 'Management' | 'Admin' }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Admin">Admin</option>
                <option value="Management">Management</option>
                <option value="Founder">Founder</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Founder role can only be set manually in Discord</p>
            </div>

            {/* Recruitment Date */}
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Recruitment Date</label>
              <input
                type="date"
                value={editingPastStaffData.recruitmentDate}
                onChange={(e) => setEditingPastStaffData(prev => ({ ...prev, recruitmentDate: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Removal Reason */}
            <div className="mb-6">
              <label className="block text-gray-300 text-sm mb-2">Removal Reason</label>
              <textarea
                value={editingPastStaffData.removalReason}
                onChange={(e) => setEditingPastStaffData(prev => ({ ...prev, removalReason: e.target.value }))}
                placeholder="Enter removal reason..."
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowEditPastStaffModal(false);
                  setEditingPastMember(null);
                  setEditingPastStaffData({ name: '', rank: 'Admin', playfabID: '', recruitmentDate: '', removalReason: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePastStaff}
                disabled={isSavingPastStaff}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isSavingPastStaff ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Remove Staff Member</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to remove <span className="text-cyan-300">{editingMember.displayName}</span> from the staff roster?
            </p>
            <div className="mb-4">
              <label className="block text-gray-300 text-sm mb-2">Removal Reason *</label>
              <textarea
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-500 focus:outline-none"
                placeholder="Enter the reason for removal..."
                rows={3}
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setRemoveReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveFromEdit}
                disabled={!removeReason.trim() || isRemovingStaff}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isRemovingStaff ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Server Modal */}
      {(showAddServerModal || showEditServerModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">
              {showAddServerModal ? 'Add New Server' : 'Edit Server'}
            </h3>
            
            <div className="space-y-4">
              {/* Server Name */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Server Name *</label>
                <input
                  type="text"
                  value={serverForm.name}
                  onChange={(e) => handleServerFormChange('name', e.target.value)}
                  placeholder="Enter server name..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Game */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Game *</label>
                <input
                  type="text"
                  value={serverForm.game}
                  onChange={(e) => handleServerFormChange('game', e.target.value)}
                  placeholder="Enter game name..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* IP Address */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">IP Address *</label>
                <input
                  type="text"
                  value={serverForm.ip}
                  onChange={(e) => handleServerFormChange('ip', e.target.value)}
                  placeholder="Enter IP address..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Port */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Port *</label>
                <input
                  type="number"
                  value={serverForm.port}
                  onChange={(e) => handleServerFormChange('port', parseInt(e.target.value))}
                  placeholder="Enter port number..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Query Type */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Query Type *</label>
                <select
                  value={serverForm.queryType}
                  onChange={(e) => handleServerFormChange('queryType', e.target.value as 'gamedig' | 'source' | 'rcon')}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="gamedig">Gamedig (Minecraft, Rust, etc.)</option>
                  <option value="source">Source Engine (CS:GO, TF2, etc.)</option>
                  <option value="rcon">RCON (Mordhau)</option>
                </select>
              </div>

              {/* RCON Password - only show when RCON is selected */}
              {serverForm.queryType === 'rcon' && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">RCON Password *</label>
                  <input
                    type="password"
                    value={serverForm.rconPassword}
                    onChange={(e) => handleServerFormChange('rconPassword', e.target.value)}
                    placeholder="Enter RCON password..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                  />
                </div>
              )}

              {/* RCON Max Players - only show when RCON is selected */}
              {serverForm.queryType === 'rcon' && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Max Player Slots *</label>
                  <input
                    type="number"
                    value={serverForm.rconMaxPlayers}
                    onChange={(e) => handleServerFormChange('rconMaxPlayers', parseInt(e.target.value) || 100)}
                    placeholder="Enter max player slots..."
                    min="1"
                    max="200"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                  />
                  <p className="text-xs text-gray-400 mt-1">Mordhau RCON doesn't provide slot info, so this must be set manually</p>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Description</label>
                <textarea
                  value={serverForm.description}
                  onChange={(e) => handleServerFormChange('description', e.target.value)}
                  placeholder="Enter server description..."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddServerModal(false);
                  setShowEditServerModal(false);
                  setEditingServer(null);
                  setServerForm({ name: '', game: '', ip: '', port: 25565, queryType: 'gamedig', rconPassword: '', rconMaxPlayers: 100, description: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveServer}
                disabled={isSavingServer || !serverForm.name.trim() || !serverForm.game.trim() || !serverForm.ip.trim()}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isSavingServer ? 'Saving...' : (showAddServerModal ? 'Add Server' : 'Update Server')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Server Modal */}
      {showDeleteServerModal && editingServer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Delete Server</h3>
            <p className="text-gray-300 mb-4">
              Are you sure you want to delete <span className="text-cyan-300">{editingServer.name}</span>?
            </p>
            <p className="text-red-400 text-sm mb-4">
              This will also delete all associated webhooks and logs. This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteServerModal(false);
                  setEditingServer(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteServer}
                disabled={isSavingServer}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isSavingServer ? 'Deleting...' : 'Delete Server'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Webhook Modal */}
      {(showAddWebhookModal || showEditWebhookModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="glass p-6 rounded-xl border border-white/10 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">
              {showAddWebhookModal ? 'Add New Webhook' : 'Edit Webhook'}
            </h3>
            
            <div className="space-y-4">
              {/* Webhook Name */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Webhook Name *</label>
                <input
                  type="text"
                  value={webhookForm.name}
                  onChange={(e) => handleWebhookFormChange('name', e.target.value)}
                  placeholder="Enter webhook name..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Webhook Type */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Category Name *</label>
                <input
                  type="text"
                  value={webhookForm.type}
                  onChange={(e) => handleWebhookFormChange('type', e.target.value)}
                  placeholder="e.g., Chat, Kills, Deaths, Joins..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Webhook Emoji */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Category Emoji *</label>
                <input
                  type="text"
                  value={webhookForm.emoji}
                  onChange={(e) => handleWebhookFormChange('emoji', e.target.value)}
                  placeholder="e.g., 💬, ⚔️, 💀, ➡️..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>

              {/* Webhook URL */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Webhook URL *</label>
                <input
                  type="url"
                  value={webhookForm.url}
                  onChange={(e) => handleWebhookFormChange('url', e.target.value)}
                  placeholder="Enter webhook URL..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddWebhookModal(false);
                  setShowEditWebhookModal(false);
                  setEditingWebhook(null);
                  setWebhookForm({ name: '', type: '', emoji: '', url: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveWebhook}
                disabled={isSavingWebhook || !webhookForm.name.trim() || !webhookForm.type.trim() || !webhookForm.emoji.trim() || !webhookForm.url.trim()}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
              >
                {isSavingWebhook ? 'Saving...' : (showAddWebhookModal ? 'Add Webhook' : 'Update Webhook')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Staff Roster Panel Component
const StaffRosterPanel: React.FC<{
  staffRoster: StaffMember[];
  pastStaff: PastStaffMember[];
  canEdit: boolean;
  onEditStaff: (member: StaffMember) => void;
  onAddStaff: () => void;
  onEditPastStaff: (member: PastStaffMember) => void;
  onRemovePastStaff: (member: PastStaffMember) => void;
  onRefreshRoster: () => void;
  searchQuery: string;
  searchResults: any[];
  isSearching: boolean;
  selectedRole: string;
  isAddingRole: boolean;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddRole: (userId: string, roleName: string) => void;
  onRoleChange: (role: 'Founder' | 'Management' | 'Admin') => void;
  showAddStaffModal: boolean;
  onExportAllPlayFabIds: () => void;
  onExportManagementPlayFabIds: () => void;
  exportDropdownRef: React.RefObject<HTMLDivElement | null>;
  showExportDropdown: boolean;
  setShowExportDropdown: (show: boolean) => void;
  onSyncAdminList: () => void;
}> = ({ 
  staffRoster, 
  pastStaff, 
  canEdit, 
  onEditStaff, 
  onAddStaff, 
  onEditPastStaff, 
  onRemovePastStaff,
  onRefreshRoster,
  searchQuery,
  searchResults,
  isSearching,
  selectedRole,
  isAddingRole,
  onSearchChange,
  onAddRole,
  onRoleChange,
  showAddStaffModal,
  onExportAllPlayFabIds,
  onExportManagementPlayFabIds,
  exportDropdownRef,
  showExportDropdown,
  setShowExportDropdown,
  onSyncAdminList
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'text-green-400';
      case 'Exempt': return 'text-blue-400';
      case 'Inactive': return 'text-red-400';
      case 'On Leave': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'Founder': return 'text-purple-400';
      case 'Management': return 'text-blue-400';
      case 'Admin': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

    // Debug logging
  
  
  return (
    <div className="space-y-8">
      {/* Current Staff Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            Current Staff Roster <span className="text-cyan-400 text-lg font-semibold">({staffRoster.length})</span>
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={onRefreshRoster}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showExportDropdown && (
                <div className="absolute right-0 mt-1 w-64 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onExportAllPlayFabIds();
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export All IDs
                    </button>
                    <button
                      onClick={() => {
                        onExportManagementPlayFabIds();
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Mgmt IDs
                    </button>
                    <div className="border-t border-gray-700 my-1"></div>
                    <button
                      onClick={() => {
                        onSyncAdminList();
                        setShowExportDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Admin List
                    </button>
                  </div>
                </div>
              )}
            </div>
            {canEdit && (
              <button
                onClick={onAddStaff}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
              >
                Add Staff Member
              </button>
            )}
          </div>
        </div>

        {/* Permission Notice */}
        {!canEdit && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-yellow-400 font-semibold">Management Access Required</h3>
                <p className="text-yellow-300 text-sm mt-1">
                  You need the Management+ Discord role to edit staff members, add new staff, or manage the roster.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="glass rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Rank</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">PlayFab ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recruitment</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  {canEdit && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {staffRoster.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-800/50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`font-medium ${getRankColor(member.rank)}`}>
                        {member.rank}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        {member.avatar && (
                          <img
                            src={member.avatar}
                            alt={member.displayName}
                            className="w-6 h-6 rounded-full mr-2"
                          />
                        )}
                        <span className="text-white text-sm">{member.displayName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-300 font-mono text-xs">
                      {member.playfabId}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-300 text-sm">
                      {member.recruitmentDate || 'N/A'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`text-sm ${getStatusColor(member.status)}`}>
                        {member.status}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onEditStaff(member)}
                            className="text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Past Staff Section */}
      {pastStaff.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">Past Staff Members</h2>
          <div className="glass rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Rank</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">PlayFab ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Recruitment</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Removal</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Reason</th>
                    {canEdit && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {pastStaff.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-800/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`font-medium ${getRankColor(member.rank)}`}>
                          {member.rank}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-300 text-sm">
                        {member.name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-300 font-mono text-xs">
                        {member.playfabID}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-300 text-sm">
                        {member.recruitmentDate}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-300 text-sm">
                        {member.removalDate}
                      </td>
                      <td className="px-3 py-2 text-gray-300 max-w-64 truncate text-sm">
                        {member.removalReason}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => onEditPastStaff(member)}
                              className="text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onRemovePastStaff(member)}
                              className="text-red-400 hover:text-red-300 transition-colors duration-200 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Staff Info Panel Component
const StaffInfoPanel: React.FC<{
  documents: any[];
  categories: string[];
  selectedCategory: string;
  canEdit: boolean;
  onCreateDocument: () => void;
  onEditDocument: (document: any) => void;
  onViewDocument: (document: any) => void;
  onDeleteDocument: (document: any) => void;
  onCategoryChange: (category: string) => void;
  onRefreshDocuments: () => void;
  getCategoryColor: (category: string) => string;
  getAccessLevelColor: (level: string) => string;
}> = ({ 
  documents, 
  categories, 
  selectedCategory, 
  canEdit, 
  onCreateDocument, 
  onEditDocument, 
  onViewDocument,
  onDeleteDocument, 
  onCategoryChange, 
  onRefreshDocuments,
  getCategoryColor,
  getAccessLevelColor
}) => {

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Staff Information Documents</h2>
        <div className="flex gap-2">
          <button
            onClick={onRefreshDocuments}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
            title="Refresh Documents"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {canEdit && (
            <button
              onClick={onCreateDocument}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
            >
              Create Document
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onCategoryChange('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
            selectedCategory === 'all' 
              ? 'bg-cyan-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All Categories
        </button>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => onCategoryChange(category)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
              selectedCategory === category 
                ? 'bg-cyan-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Documents Grid */}
      {documents.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">
            {selectedCategory === 'all' ? 'No documents found' : `No documents in "${selectedCategory}" category`}
          </div>
          <p className="text-gray-500">
            {canEdit ? 'Create your first document to get started.' : 'Documents will appear here once they are created.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((document) => (
            <div 
              key={document.id} 
              className="glass p-6 rounded-xl border border-white/10 hover:border-cyan-500/50 transition-colors duration-200 cursor-pointer"
              onClick={() => onViewDocument(document)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(document.category)} text-white`}>
                    {document.category}
                  </span>
                  <span className={`text-xs font-medium ${getAccessLevelColor(document.access_level)}`}>
                    {document.access_level}
                  </span>
                </div>
                {canEdit && (
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onEditDocument(document)}
                      className="p-1 text-gray-400 hover:text-cyan-400 transition-colors duration-200"
                      title="Edit Document"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDeleteDocument(document)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors duration-200"
                      title="Delete Document"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">{document.title}</h3>
              
              <div 
                className="text-gray-300 text-sm mb-4 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: document.content }}
              />
              
              <div className="flex justify-between items-center text-xs text-gray-400">
                <span>By {document.author_name}</span>
                <span>{new Date(document.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Logs Panel Component
const LogsPanel: React.FC<{
  gameServers: GameServer[];
  selectedServer: GameServer | null;
  webhooks: Webhook[];
  logs: LogEntry[];
  canEdit: boolean;
  onServerSelect: (server: GameServer) => void;
  onAddServer: () => void;
  onEditServer: (server: GameServer) => void;
  onDeleteServer: (server: GameServer) => void;
  onAddWebhook: () => void;
  onEditWebhook: (webhook: Webhook) => void;
  onLogFilterChange: (field: string, value: string) => void;
  onLogTabChange: (tab: string) => void;
  activeLogTab: string;
  logFilters: any;
  isLoadingLogs: boolean;
  onCheckServerStatus: (serverId: number) => void;
  onRefreshAllServers: (checkStatus: boolean) => void;
  isCheckingStatus: boolean;
  isRefreshingAll: boolean;
  refreshFeedback: string;
  hasPermission: (level: number) => boolean;
  currentPage: number;
  totalPages: number;
  totalLogs: number;
  handlePageChange: (page: number) => void;
  onRefreshLogs: () => void;
  // Quick Actions props
  quickAction: string;
  setQuickAction: (action: string) => void;
  targetPlayFabId: string;
  setTargetPlayFabId: (id: string) => void;
  targetPlayerName: string;
  setTargetPlayerName: (name: string) => void;
  reason: string;
  setReason: (reason: string) => void;
  duration: string;
  setDuration: (duration: string) => void;
  isExecutingAction: boolean;
  actionOutput: string;
  setActionOutput: (output: string) => void;
  handleQuickAction: () => void;
  // Player search props
  playerSearchResults: any[];
  isSearchingPlayers: boolean;
  handlePlayerSearch: (query: string) => void;
  selectPlayer: (player: any) => void;
  setPlayerSearchResults: (results: any[]) => void;
  // Lookup search props
  lookupSearchResults: any[];
  isSearchingLookup: boolean;
  handleLookupPlayerSearch: (query: string) => void;
  setLookupSearchResults: (results: any[]) => void;
  // Player lookup props
  lookupPlayFabId: string;
  setLookupPlayFabId: (id: string) => void;
  lookupOutput: string;
  setLookupOutput: (output: string) => void;
  isLookingUpPlayer: boolean;
  handlePlayerLookup: () => void;
  // RCON Command Line props
  rconCommand: string;
  setRconCommand: (command: string) => void;
  rconOutput: string;
  setRconOutput: (output: string) => void;
  isExecutingRcon: boolean;
  handleRconCommand: () => void;

}> = ({
  gameServers,
  selectedServer,
  webhooks,
  logs,
  canEdit,
  onServerSelect,
  onAddServer,
  onEditServer,
  onDeleteServer,
  onAddWebhook,
  onEditWebhook,
  onLogFilterChange,
  onLogTabChange,
  activeLogTab,
  logFilters,
  isLoadingLogs,
  onCheckServerStatus,
  onRefreshAllServers,
  isCheckingStatus,
  isRefreshingAll,
  refreshFeedback,
  hasPermission,
  currentPage,
  totalPages,
  totalLogs,
  handlePageChange,
  onRefreshLogs,
  // Quick Actions props
  quickAction,
  setQuickAction,
  targetPlayFabId,
  setTargetPlayFabId,
  targetPlayerName,
  setTargetPlayerName,
  reason,
  setReason,
  duration,
  setDuration,
  isExecutingAction,
  actionOutput,
  setActionOutput,
  handleQuickAction,
  // Player search props
  playerSearchResults,
  isSearchingPlayers,
  handlePlayerSearch,
  selectPlayer,
  setPlayerSearchResults,
  // Lookup search props
  lookupSearchResults,
  isSearchingLookup,
  handleLookupPlayerSearch,
  setLookupSearchResults,
  // Player lookup props
  lookupPlayFabId,
  setLookupPlayFabId,
  lookupOutput,
  setLookupOutput,
  isLookingUpPlayer,
  handlePlayerLookup,
  // RCON Command Line props
  rconCommand,
  setRconCommand,
  rconOutput,
  setRconOutput,
  isExecutingRcon,
  handleRconCommand,

}) => {
  // Ensure logs is always an array
  const safeLogs = logs || [];
  
  // Generate log tabs for RCON servers
  const logTabs = useMemo(() => {
    if (selectedServer?.queryType === 'rcon') {
      return [
        { id: 'all', name: 'All Logs', icon: '📋' },
        { id: 'chat', name: 'Chat', icon: '💬' },
        { id: 'matchstate', name: 'Matchstate', icon: '⚔️' },
        { id: 'killfeed', name: 'Kills', icon: '💀' },
        { id: 'punishment', name: 'Punishments', icon: '⚖️' },
        { id: 'login', name: 'Logins', icon: '👤' }
      ];
    } else {
      // For non-RCON servers, use webhook-based tabs
      const tabs = [
        { id: 'all', name: 'All Logs', icon: '📋' }
      ];
      
      // Add tabs for each webhook category
      webhooks.forEach(webhook => {
        if (!tabs.find(tab => tab.id === webhook.type)) {
          tabs.push({
            id: webhook.type,
            name: webhook.name,
            icon: webhook.emoji
          });
        }
      });
      
      return tabs;
    }
  }, [webhooks, selectedServer]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-400';
      case 'offline': return 'text-red-400';
      case 'maintenance': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getLogTypeColor = (type: string) => {
    const safeType = String(type || 'unknown').toLowerCase();
    
    switch (safeType) {
      case 'chat':
        return 'bg-blue-500'; // Blue for chat messages
      case 'killfeed':
      case 'kills':
        return 'bg-red-500'; // Red for kills/deaths
      case 'login':
      case 'logins':
        return 'bg-green-500'; // Green for logins
      case 'punishment':
        return 'bg-yellow-500'; // Yellow for punishments
      case 'matchstate':
        return 'bg-purple-500'; // Purple for match state changes
      default:
        return 'bg-gray-500'; // Gray for unknown types
    }
  };

  return (
    <div className="flex h-full space-x-6 overflow-hidden">
      {/* Left Sidebar - Server List */}
      <div className="w-80 flex-shrink-0 overflow-hidden">
        <div className="glass p-4 rounded-xl border border-white/10 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">Game Servers</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => onRefreshAllServers(true)}
                disabled={isRefreshingAll}
                className={`w-8 h-8 flex items-center justify-center text-white rounded-lg text-sm transition-colors duration-200 ${
                  isRefreshingAll 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                title={refreshFeedback || "Refresh All Servers"}
              >
                {isRefreshingAll ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
              {canEdit && (
                <button
                  onClick={onAddServer}
                  className="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors duration-200"
                  title="Add Server"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          <div className="space-y-2 flex-1 overflow-y-auto">
            {gameServers.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p>No servers found</p>
                {canEdit && <p className="text-sm mt-1">Add your first server to get started</p>}
              </div>
            ) : (
              gameServers.map((server) => (
                <div
                  key={server.id}
                  onClick={() => onServerSelect(server)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                    selectedServer?.id === server.id
                      ? 'bg-cyan-600/20 border border-cyan-500/50'
                      : 'bg-gray-800/50 hover:bg-gray-700/50 border border-transparent'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{server.name}</h4>
                      <p className="text-gray-300 text-sm">{server.game}</p>
                      <p className="text-gray-400 text-xs">{server.ip}:{server.port}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(server.status).replace('text-', 'bg-')}`}></div>
                      <span className={`text-xs ${getStatusColor(server.status)}`}>
                        {server.playerCount}/{server.maxPlayers}
                      </span>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="mt-2 flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditServer(server);
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteServer(server);
                        }}
                        className="text-xs text-red-400 hover:text-red-300 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 overflow-hidden">
        {selectedServer ? (
          <div className="space-y-6 h-full overflow-y-auto">
            {/* Server Info Header */}
            <div className="glass p-4 rounded-xl border border-white/10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-white">{selectedServer.name}</h2>
                  <p className="text-gray-300">{selectedServer.game} • {selectedServer.ip}:{selectedServer.port}</p>
                  <p className="text-gray-400 text-sm">{selectedServer.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-right">
                    <div>
                      <div className={`text-lg font-semibold ${getStatusColor(selectedServer.status)}`}>
                        {selectedServer.status.toUpperCase()}
                      </div>
                      <div className="text-gray-300">
                        {selectedServer.playerCount}/{selectedServer.maxPlayers} players
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Webhooks Section - Hidden for RCON servers */}
            {selectedServer?.queryType !== 'rcon' && (
              <div className="glass p-4 rounded-xl border border-white/10">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Webhooks</h3>
                  {canEdit && (
                    <button
                      onClick={onAddWebhook}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors duration-200"
                    >
                      Add Webhook
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {webhooks.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-gray-400">
                      <p>No webhooks configured</p>
                      {canEdit && <p className="text-sm mt-1">Add webhooks to receive server events</p>}
                    </div>
                  ) : (
                    webhooks.map((webhook) => (
                      <div key={webhook.id} className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-white font-medium">{webhook.name}</h4>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLogTypeColor(webhook.type)} text-white`}>
                              {webhook.type}
                            </span>
                            <div className={`w-2 h-2 rounded-full ${webhook.is_active ? 'bg-green-400' : 'bg-red-400'}`}></div>
                          </div>
                        </div>
                        <p className="text-gray-300 text-xs mb-2 truncate">{webhook.url}</p>
                        {canEdit && (
                          <button
                            onClick={() => onEditWebhook(webhook)}
                            className="text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Commands Section - Show for RCON servers to Management and above */}
            {selectedServer?.queryType === 'rcon' && hasPermission(1) && (
              <div className="glass p-4 rounded-xl border border-white/10">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Commands</h3>
                  <span className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm">Connected</span>
                </div>
                
                {/* RCON Command Line - Only show for Management and above */}
                {hasPermission(2) && (
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">💻 RCON Command Line</h4>
                    
                    {/* Command Input */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          placeholder="Enter RCON command..."
                          value={rconCommand}
                          onChange={(e) => setRconCommand(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleRconCommand()}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 font-mono"
                        />
                        <button
                          onClick={handleRconCommand}
                          disabled={!rconCommand.trim() || isExecutingRcon}
                          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                        >
                          {isExecutingRcon ? 'Executing...' : 'Execute'}
                        </button>
                      </div>
                    </div>

                    {/* Command Output */}
                    {rconOutput && (
                      <div className="mb-4">
                        <div className="bg-gray-900 p-3 rounded-lg border border-gray-700">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-400 text-sm">Command Output:</span>
                            <button
                              onClick={() => setRconOutput('')}
                              className="text-gray-400 hover:text-white text-sm"
                            >
                              Clear
                            </button>
                          </div>
                          <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                            {rconOutput}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Player Lookup */}
                <div className="mb-6">
                  <h4 className="text-white font-medium mb-3">🔍 Player Lookup</h4>
                  <div className="relative">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        placeholder="PlayFab ID or search by name..."
                        value={lookupPlayFabId}
                        onChange={(e) => {
                          setLookupPlayFabId(e.target.value);
                          // Search as user types
                          if (e.target.value.length >= 2) {
                            handleLookupPlayerSearch(e.target.value);
                          } else {
                            setLookupSearchResults([]);
                          }
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handlePlayerLookup()}
                        onFocus={() => {
                          // Clear Quick Actions search results when LookupPlayer is focused
                          setPlayerSearchResults([]);
                        }}
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                      />
                      <button
                        onClick={handlePlayerLookup}
                        disabled={!lookupPlayFabId.trim() || isLookingUpPlayer}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                      >
                        {isLookingUpPlayer ? 'Looking up...' : 'Lookup'}
                      </button>
                      </div>
                    
                    {/* Search Results Dropdown for Lookup */}
                    {lookupSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {lookupSearchResults.map((player, index) => (
                          <div
                            key={index}
                            onClick={() => {
                              setLookupPlayFabId(player.player_id);
                              setLookupSearchResults([]);
                            }}
                            className="px-3 py-2 hover:bg-gray-700 cursor-pointer text-sm"
                          >
                            <div className="text-white">{player.player_name}</div>
                            <div className="text-gray-400 text-xs font-mono">{player.player_id}</div>
                      </div>
                        ))}
                      </div>
                    )}
                      </div>
                  
                  {/* Lookup Output */}
                  {lookupOutput && (
                    <div className="mt-3">
                      <div className="bg-gray-900 p-3 rounded-lg border border-gray-700">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-400 text-sm">Lookup Result:</span>
                          <button
                            onClick={() => setLookupOutput('')}
                            className="text-gray-400 hover:text-white text-sm"
                          >
                            Clear
                          </button>
                    </div>
                        <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                          {lookupOutput}
                        </pre>
                  </div>
                        </div>
                  )}
                        </div>

                {/* Quick Actions Form */}
                <div className="mb-6">
                  <h4 className="text-white font-medium mb-3">⚡ Quick Actions</h4>
                  
                  {/* Action Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Action</label>
                      <select
                        value={quickAction}
                        onChange={(e) => setQuickAction(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      >
                        <option value="">Select an action...</option>
                        <option value="kick">Kick</option>
                        <option value="ban">Ban</option>
                        <option value="mute">Mute</option>
                        <option value="unban">Unban</option>
                        <option value="unmute">Unmute</option>
                      </select>
                        </div>
                    
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Target Player</label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="PlayFab ID or search by name..."
                          value={targetPlayFabId}
                          onChange={(e) => {
                            setTargetPlayFabId(e.target.value);
                            // Search as user types
                            if (e.target.value.length >= 2) {
                              handlePlayerSearch(e.target.value);
                            } else {
                              setPlayerSearchResults([]);
                            }
                          }}
                          onFocus={() => {
                            // Clear lookup search results when Quick Actions target is focused
                            setLookupSearchResults([]);
                          }}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                        />
                        {targetPlayerName && (
                          <div className="mt-1 text-sm text-gray-400">
                            Player: {targetPlayerName}
                        </div>
                        )}
                        
                        {/* Search Results Dropdown */}
                        {playerSearchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {playerSearchResults.map((player, index) => (
                              <div
                                key={index}
                                onClick={() => selectPlayer(player)}
                                className="p-2 hover:bg-gray-700 rounded cursor-pointer border-b border-gray-600 last:border-b-0"
                              >
                                <div className="text-white text-sm">{player.player_name}</div>
                                <div className="text-gray-400 text-xs">{player.player_id}</div>
                                <div className="text-gray-500 text-xs">Last seen: {new Date(player.last_seen).toLocaleString()}</div>
                        </div>
                            ))}
                        </div>
                        )}
                        </div>
                        </div>
                      </div>



                  {/* Action-specific fields */}
                  {(quickAction === 'kick' || quickAction === 'ban') && (
                    <div className="mb-4">
                      <label className="block text-gray-300 text-sm mb-2">Reason</label>
                      <input
                        type="text"
                        placeholder="Enter reason..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                  )}

                  {(quickAction === 'ban' || quickAction === 'mute') && (
                    <div className="mb-4">
                      <label className="block text-gray-300 text-sm mb-2">Duration (minutes, 0 = permanent)</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                      />
                </div>
                  )}

                  {/* Execute Button */}
                  <button
                    onClick={handleQuickAction}
                    disabled={!quickAction || !targetPlayFabId || isExecutingAction || 
                             (quickAction === 'kick' && !reason) || 
                             (quickAction === 'ban' && !reason)}
                    className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                  >
                    {isExecutingAction ? 'Executing...' : `Execute ${quickAction || 'Action'}`}
                  </button>
                </div>

                {/* Action Output */}
                {actionOutput && (
                  <div className="mb-4">
                    <div className="bg-gray-900 p-3 rounded-lg border border-gray-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 text-sm">Action Output:</span>
                        <button
                          onClick={() => setActionOutput('')}
                          className="text-gray-400 hover:text-white text-sm"
                        >
                          Clear
                        </button>
                      </div>
                      <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                        {actionOutput}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Help Text */}
                <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                  <p className="text-blue-300 text-sm">
                    <strong>Usage:</strong> All actions require a PlayFab ID. Use the search function to find players by name. 
                    Actions are logged with your staff information. Duration of 0 means permanent.
                  </p>
                </div>
              </div>
            )}



            {/* Logs Section */}
            <div className="glass p-4 rounded-xl border border-white/10">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-white">Logs</h3>
                  <button
                    onClick={onRefreshLogs}
                    disabled={isLoadingLogs}
                    className={`w-8 h-8 flex items-center justify-center text-white rounded-lg text-sm transition-colors duration-200 ${
                      isLoadingLogs 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-cyan-600 hover:bg-cyan-700'
                    }`}
                    title="Refresh Logs"
                  >
                    {isLoadingLogs ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <input
                    type="text"
                    placeholder="Search logs..."
                    value={logFilters.search}
                    onChange={(e) => onLogFilterChange('search', e.target.value)}
                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                  />
                  <input
                    type="text"
                    placeholder="Player name..."
                    value={logFilters.player}
                    onChange={(e) => onLogFilterChange('player', e.target.value)}
                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                  />
                  <input
                    type="date"
                    value={logFilters.dateFrom}
                    onChange={(e) => onLogFilterChange('dateFrom', e.target.value)}
                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  />
                  <input
                    type="date"
                    value={logFilters.dateTo}
                    onChange={(e) => onLogFilterChange('dateTo', e.target.value)}
                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>

              {/* Log Type Tabs */}
              <div className="flex space-x-1 mb-4 overflow-x-auto">
                {logTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => onLogTabChange(tab.id as any)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                      activeLogTab === tab.id
                        ? 'bg-cyan-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <span className="mr-1">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </div>



              {/* Logs Table */}
              <div className="overflow-x-auto">
                {isLoadingLogs ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
                    <p className="text-gray-400 mt-2">Loading logs...</p>
                  </div>
                ) : !safeLogs || safeLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <p>No logs found</p>
                    <p className="text-sm mt-1">Logs will appear here when server events occur</p>
                  </div>
                ) : (
                  <>
                    <table className="w-full table-fixed">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-300 font-medium w-32">Time</th>
                          <th className="text-left py-2 px-3 text-gray-300 font-medium w-28">Type</th>
                          <th className="text-left py-2 px-3 text-gray-300 font-medium w-32">Player</th>
                          <th className="text-left py-2 px-3 text-gray-300 font-medium w-40">PlayFabID</th>
                          <th className="text-left py-2 px-3 text-gray-300 font-medium">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeLogs.map((log) => {
                          // Safety check to ensure log object exists
                          if (!log) return null;
                          
                          // Format timestamp for user's local timezone
                          const formatTimestamp = (timestamp: string) => {
                            if (!timestamp) return 'Unknown';
                            try {
                              const date = new Date(timestamp);
                              // Use browser's built-in timezone conversion
                              const formattedDate = date.toLocaleString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false,
                                timeZoneName: 'short'
                              });
                              return formattedDate;
                            } catch (error) {
                              return 'Invalid Date';
                            }
                          };
                          
                          // Generate appropriate details based on log type
                          const getLogDetails = (log: any) => {
                            const logType = log.log_type || log.type || 'unknown';
                            
                            switch (logType) {
                              case 'chat':
                                return log.message || 'No message';
                              case 'killfeed':
                                // For killfeed, show who was killed
                                if (log.metadata && log.metadata.victimName) {
                                  return `Killed ${log.metadata.victimName} (${log.metadata.victimId || 'Unknown ID'})`;
                                }
                                return log.message || 'Kill event';
                              case 'login':
                                return log.message || 'Login event';
                              case 'punishment':
                                // Enhanced punishment details
                                let details = log.message || 'Punishment event';
                                if (log.metadata) {
                                  try {
                                    // Parse metadata if it's a string
                                    const metadata = typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
                                    console.log('Parsed metadata:', metadata); // Debug log
                                    const { action, targetPlayerId, targetPlayerName, duration, reason, source, rconExecutedAt } = metadata;
                                    if (action) {
                                      // Show both name and PlayFabID if available, otherwise just PlayFabID
                                      let targetPlayer;
                                      if (targetPlayerName && targetPlayerId) {
                                        targetPlayer = `${targetPlayerName} (${targetPlayerId})`;
                                      } else if (targetPlayerName) {
                                        targetPlayer = targetPlayerName;
                                      } else if (targetPlayerId) {
                                        targetPlayer = targetPlayerId;
                                      } else {
                                        targetPlayer = 'Unknown Player';
                                      }
                                      details = `${action} player: ${targetPlayer}`;
                                      if (duration) details += ` (${duration})`;
                                      if (reason) details += ` - ${reason}`;
                                      
                                      // Add RCON indicator if it was executed via RCON
                                      if (source === 'RCON' && rconExecutedAt) {
                                        const rconTime = new Date(rconExecutedAt).toLocaleString();
                                        details += ` [RCON: ${rconTime}]`;
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error parsing metadata:', error, 'Raw metadata:', log.metadata);
                                  }
                                }
                                return details;
                              case 'matchstate':
                                return log.message || 'Match state change';
                              default:
                                return log.message || 'No details';
                            }
                          };
                          
                          return (
                            <tr key={log.id || Math.random()} className="border-b border-gray-800 hover:bg-gray-800/30">
                              <td className="py-2 px-3 text-gray-400 text-sm">
                                {formatTimestamp(log.timestamp)}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${getLogTypeColor(log.log_type || log.type || 'unknown')} text-white`}>
                                  {(log.log_type || log.type || 'unknown') === 'punishment' ? 'Punish' : (log.log_type || log.type || 'unknown')}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-white text-sm">
                                {log.player_name || 'Unknown'}
                              </td>
                              <td className="py-2 px-3 text-gray-300 text-sm font-mono text-xs">
                                {log.player_id || 'N/A'}
                              </td>
                              <td className="py-2 px-3 text-gray-300 text-sm break-words">
                                {getLogDetails(log)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex justify-between items-center mt-4 px-3">
                        <div className="text-gray-400 text-sm">
                          Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalLogs)} of {totalLogs} logs
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                              currentPage === 1
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-600 text-white hover:bg-gray-500'
                            }`}
                          >
                            Previous
                          </button>
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                                    currentPage === pageNum
                                      ? 'bg-cyan-600 text-white'
                                      : 'bg-gray-600 text-white hover:bg-gray-500'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                              currentPage === totalPages
                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-600 text-white hover:bg-gray-500'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">No Server Selected</div>
            <p className="text-gray-500">Select a server from the list to view its logs and webhooks</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin; 